import { defaultLocale, format, type Locale } from "@bundle/i18n";
import { type DateParts, type DayLabel, type MessageDescriptor, type MessageKey, type MessageParams } from "@bundle/core";
import { createContext, useContext, useMemo, type ReactNode } from "react";

export type Translate = (key: MessageKey, params?: MessageParams) => string;

export type Formatters = {
  /** "10:42 AM" / "10:42". */
  time: (ts: number) => string;
  /** "Today", "Yesterday", "Monday, October 20", "October 22, 2025". */
  day: (label: DayLabel) => string;
  /** Full date and time (tooltips). */
  dateTime: (ts: number) => string;
  /** Database date cell: "Oct 22, 2026" / "22 de out. de 2026", with the time when it has one. */
  date: (d: DateParts) => string;
  /** Database timestamps (created/edited time): "Oct 22, 2026, 3:00 PM". */
  timestamp: (ts: number) => string;
  /** Number cells: plain, percent (0.25 → 25%) or the locale's currency (USD / BRL). */
  number: (n: number, format: "number" | "percent" | "currency") => string;
  /** Calendar title: "October 2026". */
  monthYear: (year: number, month: number) => string;
  /** Calendar column header for a weekday (0 = Sunday): "Sun". */
  weekday: (weekday: number) => string;
};

type I18nValue = { locale: Locale; t: Translate; tr: (d: MessageDescriptor) => string; fmt: Formatters };

function makeT(locale: Locale): Translate {
  return (key, params = {}) => (format as (l: Locale, k: string, p?: MessageParams) => string)(locale, key, params);
}

function makeFormatters(locale: Locale, t: Translate): Formatters {
  const time = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" });
  const full = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short" });
  const sameYear = new Intl.DateTimeFormat(locale, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
  const otherYear = new Intl.DateTimeFormat(locale, { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  const dateOnly = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  const dateWithTime = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "UTC" });
  const stamp = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  const monthYear = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  const numbers = {
    number: new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }),
    percent: new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 2 }),
    currency: new Intl.NumberFormat(locale, { style: "currency", currency: locale === "pt-BR" ? "BRL" : "USD" }),
  };
  return {
    date: (d) =>
      d.hour === undefined
        ? dateOnly.format(Date.UTC(d.year, d.month - 1, d.day))
        : dateWithTime.format(Date.UTC(d.year, d.month - 1, d.day, d.hour, d.minute ?? 0)),
    timestamp: (ts) => stamp.format(ts),
    number: (n, format) => numbers[format].format(n),
    monthYear: (year, month) => monthYear.format(Date.UTC(year, month - 1, 1)),
    // 2023-01-01 was a Sunday.
    weekday: (w) => weekday.format(Date.UTC(2023, 0, 1 + w)),
    time: (ts) => time.format(ts),
    dateTime: (ts) => full.format(ts),
    day: (label) => {
      if (label.type === "today") return t("time.today");
      if (label.type === "yesterday") return t("time.yesterday");
      const d = Date.UTC(label.year, label.month - 1, label.day);
      return (label.showYear ? otherYear : sameYear).format(d);
    },
  };
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo<I18nValue>(() => {
    const t = makeT(locale);
    return { locale, t, tr: (d) => t(d.key, d.params), fmt: makeFormatters(locale, t) };
  }, [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (v === null) throw new Error("I18nProvider missing");
  return v;
}

/** `t("channel.members", { count })` → localized string. */
export function useT(): Translate {
  return useI18n().t;
}

let defaultT: Translate | undefined;

/**
 * `t` for primitives: the app's locale inside `I18nProvider`, otherwise the default locale (galleries, tests), so a
 * primitive's built-in strings (Sheet close, Badge "3 unread", Spinner "Loading…") never need a provider to render.
 */
export function usePrimitiveT(): Translate {
  const v = useContext(I18nContext);
  if (v !== null) return v.t;
  defaultT ??= makeT(defaultLocale);
  return defaultT;
}

/** Resolves a message descriptor returned by logic / view models. */
export function useTr(): (d: MessageDescriptor) => string {
  return useI18n().tr;
}

export function useLocale(): Locale {
  return useI18n().locale;
}

export function useFormat(): Formatters {
  return useI18n().fmt;
}
